import React, { useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Button } from './ui/button'

export function TermsOfUse({ navigate }) {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/10">
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <div className="mb-12">
          <Button
            variant="ghost"
            onClick={() => {
              navigate('/')
            }}
            className="group -ml-3 px-3 rounded-full hover:bg-muted/50 transition-all active:scale-95 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
              Back to App
            </span>
          </Button>
        </div>

        <article className="prose prose-sm md:prose-base dark:prose-invert max-w-none space-y-8">
          <header className="space-y-4 border-b border-border/50 pb-8">
            <h1 className="text-4xl font-black tracking-tight md:text-5xl">Terms and Conditions</h1>
            <p className="text-muted-foreground font-medium">Last updated: March 8, 2026</p>
          </header>

          <section className="space-y-4">
            <p className="leading-relaxed text-muted-foreground/80">
              These Terms and Conditions ("Terms") govern your access to and use of the website{' '}
              <strong className="text-foreground">https://zoy.io</strong> and any related
              applications, software, or services operated by{' '}
              <strong className="text-foreground">ZOY</strong> (collectively, the "Services").
            </p>
            <p className="leading-relaxed text-muted-foreground/80 font-medium">
              By accessing or using the Services, you agree to be bound by these Terms. If you do
              not agree with these Terms, you must not use the Services.
            </p>
            <p className="leading-relaxed text-muted-foreground/80 italic border-l-2 border-primary/20 pl-4">
              Throughout these Terms, the words <strong>"ZOY"</strong>, <strong>"Company"</strong>,{' '}
              <strong>"we"</strong>, <strong>"our"</strong>, and <strong>"us"</strong> refer to the
              operators of <strong>zoy.io</strong>.
              <br />
              The words <strong>"user"</strong>, <strong>"you"</strong>, and <strong>"your"</strong>{' '}
              refer to any person or entity using the Services.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-8 border-y border-border/30">
            <div className="space-y-2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-primary/70">
                Navigation
              </h2>
              <nav className="flex flex-col gap-1 text-sm font-medium text-muted-foreground">
                <a href="#services" className="hover:text-primary transition-colors">
                  1. Services
                </a>
                <a href="#intellectual-property" className="hover:text-primary transition-colors">
                  2. Intellectual Property
                </a>
                <a href="#prohibited" className="hover:text-primary transition-colors">
                  7. Prohibited Activities
                </a>
                <a href="#liability" className="hover:text-primary transition-colors">
                  18. Limitation of Liability
                </a>
              </nav>
            </div>
          </div>

          <section id="services" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              1. Services
            </h2>
            <p className="leading-relaxed text-muted-foreground/80">
              ZOY provides software tools and online services that may be used to create, manage, or
              operate websites, applications, or related digital assets.
            </p>
            <p className="leading-relaxed text-muted-foreground/80">
              The Services are provided subject to applicable laws and regulations. You are solely
              responsible for ensuring that your use of the Services complies with all applicable
              laws in your jurisdiction.
            </p>
          </section>

          <section id="intellectual-property" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              2. Intellectual Property
            </h2>
            <p className="leading-relaxed text-muted-foreground/80">
              All intellectual property rights associated with ZOY, including but not limited to
              software, code, design, documentation, trademarks, and branding, are the exclusive
              property of ZOY or its licensors.
            </p>
            <p className="leading-relaxed text-muted-foreground/80">
              You are granted a{' '}
              <strong>limited, non-exclusive, non-transferable, non-sublicensable license</strong>{' '}
              to use the Services solely for your internal use.
            </p>
            <div className="bg-muted/30 rounded-xl p-6 border border-border/50 space-y-3">
              <p className="text-sm font-bold uppercase text-muted-foreground/60 tracking-wider">
                You may not:
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm font-medium">
                <li className="flex items-center gap-2 text-destructive/80">
                  <div className="h-1 w-1 rounded-full bg-destructive" />
                  resell the Services
                </li>
                <li className="flex items-center gap-2 text-destructive/80">
                  <div className="h-1 w-1 rounded-full bg-destructive" />
                  redistribute the software
                </li>
                <li className="flex items-center gap-2 text-destructive/80">
                  <div className="h-1 w-1 rounded-full bg-destructive" />
                  sublicense the platform
                </li>
                <li className="flex items-center gap-2 text-destructive/80">
                  <div className="h-1 w-1 rounded-full bg-destructive" />
                  reverse engineer
                </li>
              </ul>
            </div>
          </section>

          <section id="user-representations" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              3. User Representations
            </h2>
            <p className="leading-relaxed text-muted-foreground/80">
              By using the Services you represent that all information you provide is accurate, you
              have the legal capacity to enter into these Terms, and your use of the Services will
              comply with applicable laws.
            </p>
          </section>

          <section id="prohibited" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              7. Prohibited Activities
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {[
                'violate any laws',
                'attempt unauthorized access',
                'scrape or extract platform data',
                'distribute malware or malicious code',
                'interfere with the operation of the Services',
                'build or operate a product that competes with ZOY using the Services',
              ].map((activity, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-4 rounded-lg bg-secondary/20 border border-border/20"
                >
                  <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-background border text-[10px] font-bold text-muted-foreground/50">
                    {i + 1}
                  </div>
                  <p className="text-sm font-medium text-muted-foreground/80">{activity}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="liability" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              18. Limitation of Liability
            </h2>
            <p className="leading-relaxed text-muted-foreground/80 bg-destructive/5 p-4 border-l-4 border-destructive/20 rounded-r-lg italic">
              To the maximum extent permitted by law, the Services are used{' '}
              <strong className="text-foreground">entirely at your own risk</strong>.
            </p>
            <p className="leading-relaxed text-muted-foreground/80">
              ZOY and its owners, affiliates, employees, contractors, and partners shall not be
              liable for any damages arising from use or inability to use the Services, service
              interruptions, data loss, or business losses.
            </p>
          </section>

          <footer className="pt-20 border-t border-border/50 flex flex-col items-center gap-4">
            <p className="text-sm font-medium text-muted-foreground">
              © 2026 ZOY. All rights reserved.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                navigate('/')
              }}
              className="rounded-full px-8"
            >
              Back to App
            </Button>
          </footer>
        </article>
      </div>
    </div>
  )
}
